import React, { useState, useEffect, useRef } from "react";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  StarIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { FaInstagram, FaFacebook } from "react-icons/fa";
import axios from "axios";
import env from "variables/config";
import { toast } from "react-toastify";
import { FRENCH_CITIES } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";
import { Select } from "../../components/ui/Select";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("token") },
});

interface DataPoolEntry {
  _id: string;
  companyName: string;
  address?: string;
  city?: string;
  zipcode?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  website?: string;
  googleMapsUrl?: string;
  social?: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
  };
  categories?: string[];
  rating?: number;
  reviewCount?: number;
  status: string;
  importedAt: string;
}

export default function InstagramProspection() {
  const [entries, setEntries] = useState<DataPoolEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalEntries, setTotalEntries] = useState(0);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("new");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [contactFilters, setContactFilters] = useState<string[]>([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const contactDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showContactDropdown);

  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [cityInputValue, setCityInputValue] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showCityDropdown);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const cityContainerRef = useRef<HTMLDivElement>(null);

  const [hoveredPhone, setHoveredPhone] = useState<string | null>(null);
  const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
  const phonePopupRef = useRef<HTMLDivElement>(null);
  const emailPopupRef = useRef<HTMLDivElement>(null);

  const itemsPerPage = 20;

  const handleCityInput = (val: string) => {
    setCityInputValue(val);
    if (!val.trim()) {
      setCitySuggestions([]);
      setShowCityDropdown(false);
      return;
    }
    const norm = val.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const matches = FRENCH_CITIES.filter((c: string) =>
      c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(norm)
    );
    setCitySuggestions(matches.slice(0, 10));
    setShowCityDropdown(matches.length > 0);
  };

  const selectCity = (c: string) => {
    setCity(c);
    setCityInputValue(c);
    setCitySuggestions([]);
    setShowCityDropdown(false);
    cityInputRef.current?.blur();
  };

  const clearCity = () => {
    setCity("");
    setCityInputValue("");
    setCitySuggestions([]);
    setShowCityDropdown(false);
  };

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, status, contactFilters]);

  const fetchData = async (overridePage?: number) => {
    setLoading(true);
    try {
      const params: any = {
        page: overridePage ?? page,
        limit: itemsPerPage,
        status,
        hasInstagram: "true",
        categories: "restaurant",
      };
      if (search) params.search = search;
      if (city) params.city = city;
      if (contactFilters.length > 0) params.contactFilters = contactFilters.join(",");

      const res = await axios.get("/admin/data-pool", { ...config(), params });
      setEntries(res.data.data || []);
      setTotalPages(res.data.meta?.pages || 1);
      setTotalEntries(res.data.meta?.total || 0);
    } catch (error: any) {
      toast.error(error.message || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, city, contactFilters]);

  // Close dropdowns & popups on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".relative")) setShowContactDropdown(false);
      if (cityContainerRef.current && !cityContainerRef.current.contains(target)) setShowCityDropdown(false);
      if (phonePopupRef.current && !phonePopupRef.current.contains(target)) setHoveredPhone(null);
      if (emailPopupRef.current && !emailPopupRef.current.contains(target)) setHoveredEmail(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchSubmit = () => {
    setPage(1);
    fetchData(1);
  };

  const clearAllFilters = () => {
    setSearch("");
    clearCity();
    setStatus("new");
    setContactFilters([]);
    setPage(1);
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      await axios.put(
        `/admin/data-pool/${id}`,
        { status: newStatus, reviewedBy: localStorage.getItem("userId") },
        config()
      );
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Erreur");
    }
  };

  const handleCopyPhone = async (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(phone); } catch { /* ignore */ }
  };

  const handleCopyEmail = async (e: React.MouseEvent, email: string) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(email); } catch { /* ignore */ }
  };

  const activeFilterCount = [search, city, status !== "new", ...contactFilters].filter(Boolean).length;

  const goToPage = (pageNumber: number) => {
    setPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("fr-FR", { month: "short", day: "numeric", year: "numeric" });
  };

  const getTimeAgo = (dateString?: string) => {
    if (!dateString) return null;
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / 86400000);
    if (days === 0) return "Aujourd'hui";
    if (days < 7) return `Il y a ${days}j`;
    return null;
  };

  return (
    <div className="min-h-screen bg-canvas font-sans">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">

        {/* Header */}
        <div className="mb-4">
          <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">
            Prospection Instagram
          </h1>
        </div>

        {/* Controls Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
            </span>
            <span className="text-sm font-medium text-ink-muted">Données en temps réel</span>
          </div>
        </div>

        {/* Compact Filters Bar */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
              className="h-8 pl-8 pr-3 text-xs bg-surface border border-border rounded-lg text-ink placeholder:text-ink-faint hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition-colors duration-150 w-48"
            />
            <MagnifyingGlassIcon className="w-4 h-4 text-ink-faint absolute left-2.5 top-2" />
          </div>

          {/* City — restricted to cities present in the pool */}
          <div ref={cityContainerRef} className="relative">
            <MapPinIcon className="w-4 h-4 text-ink-faint absolute left-2.5 top-2 pointer-events-none z-10" />
            <input
              ref={cityInputRef}
              type="text"
              value={cityInputValue}
              onChange={(e) => handleCityInput(e.target.value)}
              onFocus={() => cityInputValue && handleCityInput(cityInputValue)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { setCity(cityInputValue.trim()); setShowCityDropdown(false); }
                if (e.key === "Escape") { setShowCityDropdown(false); }
                if (e.key === "Backspace" && !cityInputValue) clearCity();
              }}
              onBlur={() => setCity(cityInputValue.trim())}
              placeholder="Ville"
              autoComplete="off"
              className={`h-8 pl-7 pr-2.5 text-xs border rounded-lg hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent w-40 transition-colors duration-150 ${
                city ? "bg-accent-subtle text-accent border-accent/30" : "bg-surface text-ink-muted border-border"
              }`}
            />
            {showCityDropdown && citySuggestions.length > 0 && (
              <div ref={cityDropdownAlign.ref} className={`absolute top-full mt-1 ${cityDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-48 py-1 overflow-hidden`}>
                {citySuggestions.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); selectCity(c); }}
                    className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-ink hover:bg-surface-sunken transition-colors duration-150"
                  >
                    <MapPinIcon className="w-3 h-3 flex-shrink-0 text-ink-faint" />
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: "new", label: "Statut" },
              { value: "new", label: "Nouveau" },
              { value: "reviewed", label: "Examiné" },
              { value: "enriched", label: "Enrichi" },
              { value: "converted", label: "Converti" },
              { value: "ignored", label: "Ignoré" },
            ]}
            className={status !== "new" ? "bg-accent-subtle text-accent border-accent/30 hover:bg-accent-subtle/80" : ""}
          />

          {/* Coordonnées */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowContactDropdown(!showContactDropdown);
              }}
              className={`h-8 px-2.5 text-xs font-medium rounded-lg transition-colors duration-150 flex items-center gap-1 ${
                contactFilters.length > 0
                  ? "bg-accent-subtle text-accent border border-accent/30 hover:bg-accent-subtle/80"
                  : "bg-surface text-ink-muted border border-border hover:border-border-strong"
              }`}
            >
              <PhoneIcon className="w-4 h-4" />
              {contactFilters.length > 0 && (
                <span className="px-1 py-0.5 bg-accent text-white rounded-full text-[10px] font-bold">
                  {contactFilters.length}
                </span>
              )}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showContactDropdown && (
              <div ref={contactDropdownAlign.ref} className={`absolute top-full mt-1 ${contactDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[160px] py-1`}>
                {["instagram", "facebook", "email", "phone", "website"].map((contact) => (
                  <label key={contact} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken transition-colors duration-150 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contactFilters.includes(contact)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setContactFilters([...contactFilters, contact]);
                        } else {
                          setContactFilters(contactFilters.filter((f) => f !== contact));
                        }
                      }}
                      className="w-3.5 h-3.5 text-accent rounded-sm border-border"
                    />
                    <span className="text-xs text-ink capitalize">{contact}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Clear */}
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="h-8 px-2.5 text-xs font-medium text-ink-muted hover:text-danger hover:bg-danger-subtle border border-border hover:border-danger/30 rounded-lg transition-colors duration-150 flex items-center gap-1"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-ink-muted text-sm">Chargement des prospects...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 bg-surface rounded-2xl border border-border">
            <ChartBarIcon className="w-16 h-16 text-ink-faint mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-ink mb-2">Aucun prospect trouvé</h3>
            <p className="text-sm text-ink-muted max-w-md mx-auto">
              Aucun restaurant avec Instagram ne correspond à vos filtres.
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="rounded-2xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-surface-sunken">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Restaurant</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Coordonnées</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Réseaux</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Localisation</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">Importé</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-ink-faint w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-border">
                    {entries.map((entry) => {
                      const timeAgo = getTimeAgo(entry.importedAt);
                      return (
                        <tr key={entry._id} className="transition-colors duration-150 hover:bg-surface-sunken/60">

                          {/* Restaurant */}
                          <td className="px-6 py-4 align-middle">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-surface-sunken border border-border rounded-sm flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-ink-muted">
                                  {entry.companyName?.substring(0, 2).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-ink">{entry.companyName}</span>
                                {entry.categories?.[0] && (
                                  <span className="text-xs text-ink-muted">{entry.categories[0]}</span>
                                )}
                                {entry.rating && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <StarIcon className="w-3.5 h-3.5 text-warning fill-current" />
                                    <span className="text-xs font-medium text-ink">{entry.rating}</span>
                                    <span className="text-xs text-ink-muted">({entry.reviewCount} avis)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Coordonnées — icon buttons with hover popups */}
                          <td className="px-6 py-4 align-middle whitespace-nowrap">
                            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                              {/* Phone */}
                              <div className="relative">
                                <button
                                  className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                    entry.primaryPhone
                                      ? "text-ink-muted hover:text-ink hover:bg-surface-sunken"
                                      : "text-border cursor-default"
                                  }`}
                                  disabled={!entry.primaryPhone}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHoveredPhone(hoveredPhone === entry._id ? null : entry._id);
                                  }}
                                >
                                  <PhoneIcon className="w-5 h-5" />
                                </button>
                                {entry.primaryPhone && (
                                  <>
                                    <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                    {hoveredPhone === entry._id && (
                                      <div ref={phonePopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                        <div className="bg-surface border border-border rounded-2xl shadow-float p-4">
                                          <p className="text-sm font-medium text-ink mb-3">Numéro de téléphone</p>
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm text-ink">{entry.primaryPhone}</div>
                                            <button
                                              onClick={(e) => handleCopyPhone(e, entry.primaryPhone!)}
                                              className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border/60 text-ink rounded-lg transition-colors duration-150"
                                            >
                                              Copier
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* Email */}
                              <div className="relative">
                                <button
                                  className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-150 ${
                                    entry.primaryEmail
                                      ? "text-ink-muted hover:text-ink hover:bg-surface-sunken"
                                      : "text-border cursor-default"
                                  }`}
                                  disabled={!entry.primaryEmail}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHoveredEmail(hoveredEmail === entry._id ? null : entry._id);
                                  }}
                                >
                                  <EnvelopeIcon className="w-5 h-5" />
                                </button>
                                {entry.primaryEmail && (
                                  <>
                                    <span className="absolute -top-1 -right-1 bg-surface-sunken text-ink-muted text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">1</span>
                                    {hoveredEmail === entry._id && (
                                      <div ref={emailPopupRef} className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64">
                                        <div className="bg-surface border border-border rounded-2xl shadow-float p-4">
                                          <p className="text-sm font-medium text-ink mb-3">Email disponible</p>
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm text-ink truncate">{entry.primaryEmail}</div>
                                            <button
                                              onClick={(e) => handleCopyEmail(e, entry.primaryEmail!)}
                                              className="px-2 py-1 text-xs bg-surface-sunken hover:bg-border/60 text-ink rounded-lg transition-colors duration-150 flex-shrink-0"
                                            >
                                              Copier
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Réseaux — social icons */}
                          <td className="px-6 py-4 align-middle">
                            <div className="flex items-center gap-3">
                              {entry.social?.instagram ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(entry.social!.instagram, "_blank"); }}
                                  className="text-pink-500 hover:text-pink-600 transition-colors duration-150"
                                  title="Instagram"
                                >
                                  <FaInstagram className="w-5 h-5" />
                                </button>
                              ) : (
                                <FaInstagram className="w-5 h-5 text-border" />
                              )}
                              {entry.social?.facebook ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(entry.social!.facebook, "_blank"); }}
                                  className="text-info hover:text-info/80 transition-colors duration-150"
                                  title="Facebook"
                                >
                                  <FaFacebook className="w-5 h-5" />
                                </button>
                              ) : (
                                <FaFacebook className="w-5 h-5 text-border" />
                              )}
                              {entry.website && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(entry.website, "_blank"); }}
                                  className="text-ink-faint hover:text-ink-muted transition-colors duration-150"
                                  title="Site web"
                                >
                                  <GlobeAltIcon className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Localisation */}
                          <td className="px-6 py-4 align-middle">
                            {(entry.city || entry.zipcode) && (
                              <div className="flex items-center gap-1 text-sm text-ink-muted">
                                <MapPinIcon className="w-4 h-4 text-ink-faint flex-shrink-0" />
                                <span>{entry.city}{entry.zipcode && `, ${entry.zipcode}`}</span>
                              </div>
                            )}
                            {entry.googleMapsUrl && (
                              <button
                                onClick={(e) => { e.stopPropagation(); window.open(entry.googleMapsUrl, "_blank"); }}
                                className="text-xs text-ink-faint hover:text-ink hover:underline mt-0.5 transition-colors duration-150"
                              >
                                Voir sur Maps
                              </button>
                            )}
                          </td>

                          {/* Importé */}
                          <td className="px-6 py-4 align-middle whitespace-nowrap">
                            {timeAgo ? (
                              <span className="text-sm text-ink font-medium">{timeAgo}</span>
                            ) : (
                              <span className="text-sm text-ink-muted">{formatDate(entry.importedAt)}</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center">
                              <div className="inline-flex border border-border rounded-lg overflow-hidden">
                                <button
                                  onClick={() => handleUpdateStatus(entry._id, "reviewed")}
                                  disabled={entry.status === "reviewed"}
                                  className={`p-2 transition-colors duration-150 border-r border-border ${
                                    entry.status === "reviewed"
                                      ? "text-success bg-success-subtle hover:bg-success-subtle/80"
                                      : "text-ink-faint hover:text-success hover:bg-success-subtle"
                                  }`}
                                  title="Marquer comme examiné"
                                >
                                  <HandThumbUpIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(entry._id, "ignored")}
                                  className={`p-2 transition-colors duration-150 ${
                                    entry.status === "ignored"
                                      ? "text-danger bg-danger-subtle hover:bg-danger-subtle/80"
                                      : "text-ink-faint hover:text-danger hover:bg-danger-subtle"
                                  }`}
                                  title="Ignorer"
                                >
                                  <HandThumbDownIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-border pt-3">
                <div className="text-xs text-ink-muted">
                  <span className="font-medium text-ink">{((page - 1) * itemsPerPage) + 1}</span>–
                  <span className="font-medium text-ink">{Math.min(page * itemsPerPage, totalEntries)}</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg bg-surface-sunken hover:bg-border/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-ink-muted" />
                  </button>

                  <div className="flex items-center gap-0.5">
                    {getPageNumbers().map((pageNum, idx) =>
                      pageNum === "..." ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-ink-faint">...</span>
                      ) : (
                        <button
                          key={idx}
                          onClick={() => goToPage(pageNum as number)}
                          className={`min-w-[32px] h-8 px-2 rounded-lg font-medium text-xs transition-colors duration-150 ${
                            page === pageNum ? "bg-ink text-ink-inverse" : "bg-surface-sunken text-ink-muted hover:bg-border/60"
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg bg-surface-sunken hover:bg-border/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-ink-muted" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
